import DOMPurify from 'dompurify';

/**
 * Tags allowed in sanitized HTML. The goal is to support rich-text content
 * from CKEditor / similar CMS editors (blog, pengumuman, kebijakan privasi)
 * while blocking any markup that could execute script or exfiltrate data.
 *
 * Blocked: script, iframe, object, embed, form, input, button, select,
 *          textarea, style, link, meta, base, frame, frameset, applet.
 *          (DOMPurify removes these by default; we list them explicitly
 *           via FORBID_TAGS below for defense in depth.)
 */
const ALLOWED_TAGS = [
  // Text formatting
  'p', 'br', 'hr', 'b', 'i', 'u', 's', 'strong', 'em', 'sub', 'sup',
  'small', 'mark', 'del', 'ins', 'span', 'div',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li',
  // Quotes & code
  'blockquote', 'q', 'cite', 'pre', 'code', 'kbd', 'samp', 'var',
  // Tables (for spreadsheet-style data)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // Media
  'img', 'figure', 'figcaption', 'audio', 'video', 'source',
  // Links
  'a',
];

/**
 * Attributes allowed. We deliberately exclude event-handler attributes
 * (onclick, onerror, onload, etc.) — DOMPurify blocks these by default,
 * but FORBID_ATTR makes it explicit.
 */
const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'width', 'height',
  'class', 'id', 'style', 'lang', 'dir',
  'colspan', 'rowspan', 'scope',
  'target', 'rel', 'name',
  'controls', 'poster', 'preload',
  'data-*',
];

/**
 * Sanitize a string of HTML for safe rendering with dangerouslySetInnerHTML.
 *
 * This is a thin wrapper around DOMPurify with a project-specific
 * allowlist. The returned object is `{ __html: ... }` so it can be
 * passed directly to `dangerouslySetInnerHTML`:
 *
 *   <div dangerouslySetInnerHTML={sanitizeHTML(content)} />
 *
 * For a raw sanitized string (e.g. to pass to `e.target.innerText`
 * for contentEditable cells), use `sanitizeText` instead.
 *
 * Configuration rationale:
 * - ALLOWED_TAGS / ALLOWED_ATTR: explicit allowlist (safer than blocklist).
 * - FORBID_TAGS / FORBID_ATTR: redundant safety net; the spec is
 *   "default-allow script-like tags" so we list them in forbid to make
 *   intent clear.
 * - ALLOW_DATA_ATTR: keeps data-* (used by some CKEditor plugins).
 * - KEEP_CONTENT: preserves text inside removed tags (e.g. `<script>foo</script>`
 *   becomes `foo`), avoiding silent data loss for content authors.
 * - ADD_ATTR: ['target'] so links can open in a new tab; we strip the
 *   target attribute below and re-add it with a safe `rel` for security.
 */
const sanitizeHTML = (dirty) => ({
  __html: DOMPurify.sanitize(dirty ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    KEEP_CONTENT: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'style', 'link', 'meta', 'base', 'frame', 'frameset', 'applet', 'svg', 'math'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onkeydown', 'onkeyup', 'onkeypress', 'onsubmit', 'onchange', 'oninput', 'formaction', 'action', 'srcdoc'],
  }),
});

/**
 * Strip ALL HTML markup and return plain text. Useful for:
 * - contentEditable cells (store the user's edited text without markup)
 * - safely embedding user content as text-only
 *
 * Uses the browser's DOMParser when available; otherwise falls back to
 * a regex-based stripper for SSR / Node environments.
 */
const stripHTML = (html) => {
  if (html === null || html === undefined) return '';
  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').trim();
  }
  // Fallback: naive regex strip
  return String(html).replace(/<[^>]*>/g, '');
};

/**
 * Sanitize a string of HTML and return the sanitized HTML as a plain
 * string (NOT wrapped in `{ __html }`). Useful for:
 * - Storing user-edited content from contentEditable cells
 *   (`e.target.innerHTML` returns raw HTML; sanitize before saving)
 * - Composing sanitized fragments before passing to dangerouslySetInnerHTML
 *
 * Same allowlist as `sanitizeHTML`.
 */
const sanitizeHTMLString = (dirty) => {
  return DOMPurify.sanitize(dirty ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    KEEP_CONTENT: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'style', 'link', 'meta', 'base', 'frame', 'frameset', 'applet', 'svg', 'math'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onkeydown', 'onkeyup', 'onkeypress', 'onsubmit', 'onchange', 'oninput', 'formaction', 'action', 'srcdoc'],
  });
};

/**
 * Sanitize and force-safe links. Wraps each `<a target="_blank">` to add
 * `rel="noopener noreferrer"` to prevent window.opener attacks.
 *
 * Use this when you need to keep target="_blank" links but want them
 * hardened. DOMPurify by default strips target attributes when sanitizing;
 * we re-add the target via the FORCED_ATTR below.
 *
 * Currently unused by callers but exported for future use when we need
 * to preserve target="_blank" in the rendered HTML.
 */
const sanitizeHTMLWithSafeLinks = (dirty) => {
  const cleaned = DOMPurify.sanitize(dirty ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'target'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout'],
  });
  // Add rel="noopener noreferrer" to all target="_blank" links.
  return cleaned.replace(
    /<a\s+([^>]*?)target=["']_blank["']([^>]*)>/gi,
    '<a $1target="_blank" rel="noopener noreferrer"$2>'
  );
};

export { sanitizeHTML, sanitizeHTMLString, stripHTML, sanitizeHTMLWithSafeLinks };
export default sanitizeHTML;
